import React, {
  forwardRef,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Calendar as CalendarIcon,
  Eye as EyeIcon,
  File as FileIcon,
  FileText as FileTextIcon,
  Info as InfoIcon,
  Trash2 as Trash2Icon,
} from 'react-feather'
import { isEmpty, last, map } from 'lodash'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core'

import { AlertDialog } from 'components'

import { EvidenceQuestion } from '..'

import helpers from 'helpers'
import * as service from 'service'
import theme from 'theme'

import useAuth from 'hooks/useAuth'
import useSnackbar from 'hooks/useSnackbar'
import useStyles from './styles'

const EvidencePlanAction = forwardRef(
  (
    {
      changesManagementId,
      evidencePlanAction,
      defaultEvidencePlanAction,
      disabledAll,
      canEditActivity,
      setLoading,
      componentIndex,
      removeEvidencePlanAction,
      isCreatedManually = false,
    },
    ref,
  ) => {
    const evidence = evidencePlanAction?.evidence
    const snackbar = useSnackbar()
    const classes = useStyles()

    const { handleRemove } = helpers.file
    const { user } = useAuth()

    const { control, errors, setValue, watch } = useFormContext()
    const name = `evidencePlanActions[${componentIndex}]`
    const attachments = watch(`${name}.evidence.attachments`)

    const componentError = errors?.evidencePlanActions?.[componentIndex]

    const [openDestroyDocument, setOpenDestroyDocument] = useState(false)
    const [currentDocumentId, setCurrentDocumentId] = useState(undefined)
    const [openRemoveEvidence, setOpenRemoveEvidence] = useState(false)
    const [existingAttachments, setExistingAttachments] = useState(
      defaultEvidencePlanAction?.evidence?.attachments ?? [],
    )
    const inputFileRef = useRef(null)

    const currentAttachments = useMemo(() => {
      return [...(attachments ?? []), ...existingAttachments]
    }, [attachments, existingAttachments])

    const evidenceDisabled = useMemo(() => {
      return map(
        evidencePlanAction?.planActions,
        (planAction) => planAction?.responsible?.id,
      ).includes(user?.id)
    }, [evidencePlanAction?.planActions, user?.id])

    useEffect(() => {
      setValue(name, evidencePlanAction)

      //eslint-disable-next-line
    }, [name])

    const serverAttachmentIds = (
      defaultEvidencePlanAction?.evidence?.attachments ?? []
    )
      .map((a) => a.id)
      .join(',')

    useEffect(() => {
      setExistingAttachments(
        defaultEvidencePlanAction?.evidence?.attachments ?? [],
      )
      setValue(`${name}.evidence.attachments`, [])
      // eslint-disable-next-line
    }, [serverAttachmentIds])

    const disabledInputs =
      (!evidenceDisabled && !canEditActivity) || disabledAll

    const handleConfirmRemoveEvidence = async () => {
      setOpenRemoveEvidence(false)
      const originId = evidencePlanAction?.originId
      if (originId) {
        try {
          setLoading(true)
          await service.dponet.changesManagement.destroyEvidencePlanAction({
            changesManagementId,
            evidencePlanActionId: originId,
          })
        } catch (error) {
          snackbar.open({
            message: helpers.formatters.errorMessage(
              error?.response?.data?.error,
            ),
            variant: 'error',
          })
          setLoading(false)
          return
        } finally {
          setLoading(false)
        }
      }
      removeEvidencePlanAction()
    }

    const handleOpenUrl = (url) => {
      if (!url || typeof url !== 'string' || !url.startsWith('http')) return
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    const handleOpenRemoveDocument = (documentId) => {
      setCurrentDocumentId(documentId)
      setOpenDestroyDocument(true)
    }

    const handleRemoveDocument = async () => {
      try {
        setLoading(true)

        await service.dponet.changesManagement.removeEvidenceDocument({
          changesManagementId,
          evidencePlanActionId: evidencePlanAction?.originId,
          documentId: currentDocumentId,
        })

        setExistingAttachments((prev) =>
          prev?.filter((attachment) => attachment?.id !== currentDocumentId),
        )
        snackbar.open({
          message: 'Documento removido com sucesso!',
          variant: 'success',
        })
      } catch (error) {
        snackbar.open({
          message: helpers.formatters.errorMessage(
            error?.response?.data?.error,
          ),
          variant: 'error',
        })
      } finally {
        setOpenDestroyDocument(false)
        setCurrentDocumentId(undefined)
        setLoading(false)
      }
    }

    const handleOpenFile = (attachment) => {
      if (attachment?.url) {
        return handleOpenUrl(attachment?.url)
      }

      try {
        const fileURL = URL.createObjectURL(attachment)
        handleOpenUrl(fileURL)
      } catch (error) {
        snackbar.open({
          message: 'Não foi possível abrir o arquivo.',
          variant: 'error',
        })
      }
    }

    return (
      <Paper variant="outlined" className={classes.sectionPaper} ref={ref}>
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
          <Box display="flex" alignItems="center" gridGap={theme.spacing(2)}>
            <Controller
              as={
                <TextField
                  label="Nome da Evidência *"
                  disabled={disabledInputs}
                  variant="outlined"
                  error={!!componentError?.evidence?.title}
                  helperText={componentError?.evidence?.title?.message}
                  className={classes.whiteInput}
                  fullWidth
                />
              }
              name={`${name}.evidence.title`}
              control={control}
              defaultValue={evidence?.title ?? ''}
            />
            {isCreatedManually && (
              <IconButton
                onClick={() => setOpenRemoveEvidence(true)}
                disabled={disabledInputs}
              >
                <Trash2Icon />
              </IconButton>
            )}
          </Box>
          <Controller
            as={
              <TextField
                label="Descrição da Evidência *"
                disabled={disabledInputs}
                variant="outlined"
                multiline
                minRows={4}
                maxRows={18}
                error={!!componentError?.evidence?.description}
                helperText={componentError?.evidence?.description?.message}
                className={classes.whiteInput}
                fullWidth
              />
            }
            name={`${name}.evidence.description`}
            control={control}
            defaultValue={evidence?.description ?? ''}
          />
        </Box>
        {!isEmpty(currentAttachments) && !!evidence?.updatedAt && (
          <Box className={classes.evidenceDate}>
            <CalendarIcon size={20} />
            <Typography variant="h5">Evidência enviada em:</Typography>
            <Typography variant="h6">
              {helpers.formatters.dates.ptBrFormatDateTime(evidence?.updatedAt)}
            </Typography>
          </Box>
        )}
        <Controller
          as={<Fragment />}
          name={`${name}.originId`}
          control={control}
        />
        <Controller
          as={
            <TextField
              label="Justificativa / Comentários *"
              className={classes.whiteInput}
              disabled={disabledInputs}
              variant="outlined"
              error={!!componentError?.evidence?.justify}
              helperText={componentError?.evidence?.justify?.message}
              multiline
              minRows={4}
              maxRows={18}
              placeholder="Utilize este espaço para explicar ou comentar a evidência. As informações inseridas aqui serão exibidas posteriormente no relatório."
              fullWidth
            />
          }
          name={`${name}.evidence.justify`}
          control={control}
          defaultValue={evidence?.justify ?? ''}
        />

        {!isEmpty(currentAttachments) && (
          <Paper variant="outlined">
            <PerfectScrollbar
              options={{
                wheelPropagation: true,
                useBothWheelAxes: false,
              }}
              className={classes.attachmentItems}
            >
              {Array.from(currentAttachments)?.map((attachment, index, arr) => (
                <Box key={index}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gridGap={2}
                    px={2}
                    py={1}
                  >
                    <Box
                      display="flex"
                      alignContent="center"
                      gridGap={theme.spacing(2)}
                    >
                      <FileIcon size={20} />
                      <Typography>{attachment?.name}</Typography>
                    </Box>
                    <Box display="flex">
                      <Box>
                        <IconButton
                          variant="contained"
                          onClick={() => handleOpenFile(attachment)}
                        >
                          <EyeIcon size={20} />
                        </IconButton>
                      </Box>
                      {!disabledInputs && (
                        <IconButton
                          disabled={disabledInputs}
                          onClick={(event) =>
                            attachment?.id
                              ? handleOpenRemoveDocument(attachment?.id)
                              : handleRemove({
                                  attachments,
                                  controlName: `${name}.evidence.attachments`,
                                  event,
                                  index,
                                  setValue,
                                  inputRef: inputFileRef,
                                })
                          }
                        >
                          <Trash2Icon size={20} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  {attachment !== last(arr) && <Divider />}
                </Box>
              ))}
            </PerfectScrollbar>
          </Paper>
        )}
        {/* Documentos modelos */}
        {!isEmpty(evidence?.modelDocuments) && (
          <Fragment>
            <Box
              display="flex"
              alignItems="center"
              gridGap={theme.spacing(1)}
              justifyContent="space-between"
            >
              <Typography variant="h6">Documentos de referência</Typography>
              <Tooltip
                title="Documentos de referência são modelos ou guias que podem ser utilizados como base para a elaboração da evidência."
                arrow
              >
                <InfoIcon size={16} cursor="help" />
              </Tooltip>
            </Box>
            <Paper variant="outlined">
              <PerfectScrollbar
                options={{
                  wheelPropagation: true,
                  useBothWheelAxes: false,
                }}
                className={classes.attachmentItems}
              >
                {evidence?.modelDocuments?.map((modelDocument, index, arr) => (
                  <Box key={index}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      px={2}
                      py={1}
                    >
                      <Box
                        display="flex"
                        alignContent="center"
                        gridGap={theme.spacing(1)}
                      >
                        <FileTextIcon size={20} />
                        <Typography>{modelDocument?.name}</Typography>
                      </Box>
                      <Box>
                        <IconButton
                          variant="contained"
                          onClick={() => handleOpenUrl(modelDocument?.url)}
                        >
                          <EyeIcon size={20} />
                        </IconButton>
                      </Box>
                    </Box>
                    {modelDocument !== last(arr) && <Divider />}
                  </Box>
                ))}
              </PerfectScrollbar>
            </Paper>
          </Fragment>
        )}
        <EvidenceQuestion
          componentIndex={componentIndex}
          evidencePlanAction={defaultEvidencePlanAction}
          evidencePlanActionId={evidencePlanAction?.originId}
          changesManagementId={changesManagementId}
          disabledInputs={disabledInputs}
          inputFileRef={inputFileRef}
          setLoading={setLoading}
        />
        <AlertDialog
          open={openRemoveEvidence}
          onClose={() => setOpenRemoveEvidence(false)}
          variant="standard"
          severity="warning"
          content="Tem certeza de que deseja excluir esta evidência? Essa ação não poderá ser desfeita!"
          dialogActions={
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenRemoveEvidence(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmRemoveEvidence}
              >
                Excluir
              </Button>
            </>
          }
        />
        <AlertDialog
          open={openDestroyDocument}
          onClose={() => setOpenDestroyDocument(false)}
          variant="standard"
          severity="warning"
          content="Tem certeza de que deseja excluir este documento? Essa ação não poderá ser desfeita!"
          dialogActions={
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setOpenDestroyDocument(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleRemoveDocument}
              >
                Excluir
              </Button>
            </>
          }
        />
      </Paper>
    )
  },
)

export default EvidencePlanAction
